/**
 * History (Settings, section 3): every publish with who, when and what it changed, a way back to
 * an earlier version (with a confirm that names the effect), and the team's activity in words.
 * Version numbers stay out of sight; a version is named by its date.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import Button from "@web-ui/components/Button";
import Empty from "@web-ui/components/Empty";
import ListRow from "@web-ui/components/ListRow";
import Modal from "@web-ui/components/Modal";
import Notice from "@web-ui/components/Notice";
import { people, publishing, type AuditEntry, type PublishRecord } from "@/api/admin";
import { dateTime, relative } from "@/lib/format";
import { describeActivity, historySummary } from "@/publishing/changes";
import { useStudio } from "@/shell/studio";

/** Who published each version: the audit log names the person behind every publish and restore. */
export function publishersOf(entries: AuditEntry[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const e of entries) if ((e.action === "publish" || e.action === "rollback") && e.targetId && e.actorEmail) map.set(e.targetId, e.actorEmail === "cli" ? "the command line" : e.actorEmail);
  return map;
}

export function History() {
  const { client, pack, canEdit, refresh, labelOf } = useStudio();
  const [records, setRecords] = useState<PublishRecord[] | null>(null);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [more, setMore] = useState(false);
  const [confirm, setConfirm] = useState<PublishRecord | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: "positive" | "critical"; text: string } | null>(null);

  const load = useCallback(async () => {
    const [h, a] = await Promise.all([publishing.history(client), people.audit(client)]);
    setRecords(h.publishes); setAudit(a.entries); setMore(a.entries.length === 100);
  }, [client]);
  useEffect(() => { void load().catch(() => setRecords([])); }, [load]);

  const publishers = useMemo(() => publishersOf(audit), [audit]);
  const byVersion = useMemo(() => new Map((records ?? []).map((r) => [r.version, r])), [records]);

  const restore = async (record: PublishRecord) => {
    setBusy(true);
    try {
      await publishing.rollback(client, record.version);
      setMessage({ tone: "positive", text: `The apps go back to the version from ${dateTime(record.publishedAt)} within a minute.` });
      setConfirm(null);
      await Promise.all([load(), refresh()]);
    } catch { setMessage({ tone: "critical", text: "That version was not restored. Nothing changed in the apps." }); }
    finally { setBusy(false); }
  };

  const older = async () => {
    const { entries } = await people.audit(client, audit.at(-1)?.at);
    setAudit((all) => [...all, ...entries]); setMore(entries.length === 100);
  };

  return (
    <>
      {message ? <Notice tone={message.tone} onDismiss={() => setMessage(null)}>{message.text}</Notice> : null}
      <section className="card flex flex-col gap-3" aria-labelledby="publishes">
        <div className="ps-card-inset">
          <h2 id="publishes" className="text-sub-title-1">Publishes</h2>
          <p className="text-body-2 text-t-secondary">Each time the apps got a new version, newest first.</p>
        </div>
        {records === null ? <p className="p-4 text-body-2 text-t-secondary" aria-busy="true">Loading…</p>
          : records.length === 0 ? <Empty icon="send" title="Nothing published yet" description="Your first publish appears here, with who published it and what changed." />
          : (
            <div className="flex flex-col">
              {records.map((r, i) => {
                const source = r.sourceVersion ? byVersion.get(r.sourceVersion) : undefined;
                const line = historySummary(r, records[i + 1] ?? null, source ? dateTime(source.publishedAt) : undefined);
                const who = r.kind === "refresh" ? "automatically" : publishers.get(String(r.version)) ? `by ${publishers.get(String(r.version))}` : "";
                return (
                  <ListRow key={r.version}
                    icon={line.kind === "restore" ? "reset" : line.kind === "refresh" ? "clock" : "send"} tone={i === 0 ? "positive" : "neutral"}
                    title={<>{line.title}{i === 0 ? <span className="text-t-tertiary"> · in the apps now</span> : null}</>}
                    detail={`${dateTime(r.publishedAt)}${who ? ` ${who}` : ""} · ${line.detail}`}
                    actions={canEdit && i > 0 ? <Button type="button" size="sm" variant="tertiary" onClick={() => setConfirm(r)}>Restore this version</Button> : undefined} />
                );
              })}
            </div>
          )}
      </section>

      <section className="card flex flex-col gap-3" aria-labelledby="activity">
        <div className="ps-card-inset">
          <h2 id="activity" className="text-sub-title-1">Activity</h2>
          <p className="text-body-2 text-t-secondary">What the team did, and when.</p>
        </div>
        <div className="flex flex-col">
          {audit.map((e) => (
            <ListRow key={e.id} title={describeActivity(e, pack, labelOf)} meta={<span className="text-caption text-t-tertiary" title={dateTime(e.at)}>{relative(e.at)}</span>} />
          ))}
        </div>
        {more ? <div><Button type="button" variant="outline" size="sm" onClick={() => void older()}>Show older</Button></div> : null}
      </section>

      <Modal open={!!confirm} onClose={() => setConfirm(null)} size="sm">
        {confirm ? (
          <div className="flex flex-col gap-4">
            <h2 className="text-h5">Restore the version from {dateTime(confirm.publishedAt)}?</h2>
            <p className="text-body-2 text-t-secondary">
              The apps go back to what they showed after that publish, within a minute. Nothing in the console changes: what you saved since stays, shows as “Changes not live”, and goes out at your next publish.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button variant="primary" type="button" isPending={busy} onClick={() => void restore(confirm)}>Restore this version</Button>
              <Button variant="outline" type="button" onClick={() => setConfirm(null)}>Cancel</Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </>
  );
}
